// @flow

import * as React from 'react';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  selectCartCurrency,
  removeProducts,
  useDiscountDetails,
  type ProductError as CartProductError,
  type MailboxType,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import {
  selectMailboxesPrices,
  type MailboxPricesByType,
  mailboxes,
} from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Base, Fqdn } from '@gandi/ui';

import {
  fetchMailboxesPrices,
  addOrRemoveMailboxProducts,
} from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow } from '../../ListRow';

import { selectFirstMailboxProductIdsByFqdn } from '../selectors';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import { CountRow, TitleRow } from './components/Rows';
import { AutoRenewRow } from './components/Rows/AutoRenewRow';
import { DiscountRow } from './components/Rows/DiscountRow';
import TotalRow from './components/Rows/TotalRow';
import { updateMailboxCreatePayloadByFqdn } from './modules/mailbox';

type Props = {
  product: DecoratedProduct,
};

export default function MailboxCreate({ product }: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();
  const dispatch = useDispatch();
  const { __, formatDate, formatPrice } = useTranslator();

  const [loading, setLoading] = React.useState(false);

  const currency = useAppSelector(selectCartCurrency);

  const firstMailboxProductIdsByFqdn = useAppSelector(
    selectFirstMailboxProductIdsByFqdn,
  );
  const showTitle = firstMailboxProductIdsByFqdn.includes(product.id);

  const mailboxPriceParams = {
    process: 'create',
    productType: 'mailboxv2',
  };

  const prices: MailboxPricesByType = useAppSelector(state =>
    selectMailboxesPrices(state, mailboxPriceParams),
  );

  const discountDetails = useDiscountDetails({ product });

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_create',
    ),
  );
  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_create',
    ),
  );

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  /**
   * Add or remove product into the cart.
   * Quantity is the quantity (string) or an event when used via
   * `inputProps` in the Counter component.
   */
  const onCounterChange = (quantity: number) => {
    // there's a mismatch between quantity and product.quantity, which leads to quantity not being updated when it should
    if (loading) {
      return;
    }
    setLoading(true);

    const fqdn = product.fqdn;
    // $FlowFixMe[incompatible-use] mailboxType does not exist in DecoratedProduct
    const mailboxType: MailboxType = product.mailboxType;
    if (fqdn) {
      // dispatch(addOrRemoveMailboxProducts(fqdn, Number(quantity), mailboxType))
      dispatch(
        addOrRemoveMailboxProducts({
          fqdn,
          mailboxType,
          process: product.process,
          quantity: Number(quantity),
        }),
      )
        .catch(() => {
          dispatch(
            notifyDanger({
              message: __('An error has occurred. Please try again.'),
            }),
          );
        })
        .then(() => {
          return dispatch(getCart());
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fqdn = product.payload.fqdn || '';
  const onAutoRenewChange = (evt: SyntheticInputEvent<>) => {
    if (!product.payload.mailbox_type) return Promise.resolve();

    return dispatch(
      updateMailboxCreatePayloadByFqdn({
        fqdn,
        mailbox_type: product.payload.mailbox_type,
        payload: { autorenew: evt.target.checked },
      }),
    ).then(() => dispatch(getCart()));
  };

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const getPriceByMonth = () => {
    const {
      payload: { mailbox_type },
    } = product;

    if (!isEmpty(prices) && mailbox_type) {
      return get(prices, [mailbox_type, 'price']);
    }

    return undefined;
  };

  const label = fqdn ? <Fqdn>{fqdn}</Fqdn> : __('Mailbox');

  if (productErrors.length > 0) {
    return <ProductErrorItem label={label} errors={productErrors} />;
  }

  const mailboxType = product.payload.mailbox_type;
  const capacity = product.payload.capacity;
  const priceByMonth = getPriceByMonth();
  const expirationDate = product.expirationDate;

  const detail =
    !!priceByMonth &&
    __('%(montlyPrice)s/ month/ mailbox', {
      montlyPrice: formatPrice(priceByMonth, currency),
    });

  const price = product.prices?.length
    ? product.computedTotalPrices?.beforeDiscount ||
      product.total?.before_discount
    : undefined;

  const normalPrice =
    mailboxType && !!product.payload.duration
      ? get(
          prices,
          `${mailboxType}.${product.payload.duration}.normalPriceBeforeTaxes`,
          undefined,
        )
      : undefined;

  const countRowLabelLegacy =
    mailboxType === 'standard' ? __('Mailbox 3 GB') : __('Mailbox 50 GB');

  return (
    <ProductItemBase
      header={
        <>
          {showTitle && (
            <TitleRow
              label={label}
              productType={product.productType}
              info={
                expirationDate &&
                __('Expires on %(dateEnd)s', {
                  dateEnd: formatDate(expirationDate, 'LL'),
                })
              }
              handleRemoveAction={removeFromCart}
            />
          )}
          <CountRow
            productId={product.id}
            count={product.quantity}
            label={
              capacity
                ? __('Mailbox %(name)s %(capacity)s %(plan)s', {
                    capacity,
                    // XXX: why name can be void? may improve DecoratedProduct type or improve and use `MailboxProduct` type?
                    name: product.payload.name || '',
                    plan:
                      product.payload.duration === 1
                        ? __('monthly subscription')
                        : __('yearly subscription'),
                  })
                : countRowLabelLegacy
            }
            price={loading ? undefined : price}
            priceCrossedOut={
              loading || normalPrice === undefined
                ? undefined
                : normalPrice * product.quantity
            }
            showExcludeVatMention={product.hasTaxes}
            onChange={onCounterChange}
            isLoading={loading}
            detail={detail}
          />

          {!FF_CART_OVERVIEW_NDS && (
            <Base marginBottom="xsmall">
              <AutoRenewRow
                productId={product.id}
                checked={product.payload?.autorenew || false}
                onChange={onAutoRenewChange}
              />
            </Base>
          )}
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={
            product.computedTotalPrices?.beforeTaxes ??
            product.total?.before_taxes
          }
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          // $FlowFixMe[incompatible-type] - price can be a string
          price={discount.price}
        />
      ))}
      {!!detail && FF_CART_OVERVIEW_NDS && (
        <ListRow
          intent="muted"
          icon={<CornerDownRight size={16} strokeWidth={1.25} />}
          title={detail}
          optionsArrow={false}
        />
      )}
    </ProductItemBase>
  );
}
